import { useState, useEffect, useCallback } from 'react';
import { useProgram } from './useProgram';
import { PublicKey } from '@solana/web3.js';
import { Buffer } from 'buffer'

interface PoolState {
  admin_authority: string;
  monopoly_mint: string;
  roll_mint: string;
  monopoly_stake_vault: string;
  monopoly_fee_vault: string;
  total_staked: number;
  reward_rate: number;
  last_update_timestamp: number;
  global_reward_per_token: bigint; 
  fee_percentage: number;
}

export function usePoolState() {
  const { connection, programId, monopolyMint, rollMint } = useProgram();
  const [poolState, setPoolState] = useState<PoolState | null>(null);
  const [loading, setLoading] = useState(true);

  const derivePoolPDA = useCallback(async () => {
    const [pda] = await PublicKey.findProgramAddress(
      [Buffer.from('pool3'), monopolyMint.toBuffer(), rollMint.toBuffer()],
      programId
    );
    return pda;
  }, [programId, monopolyMint, rollMint]);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const pda = await derivePoolPDA();
      const accountInfo = await connection.getAccountInfo(pda);
      if (!accountInfo) {
        setPoolState(null);
        setLoading(false);
        return;
      }

      // Parse the account data according to your PoolState struct layout.
      // This depends on the exact binary layout. The below is pseudo-parsing:
      const data = accountInfo.data;

      // Example parsing (adjust offsets and data types based on your actual struct):
      // This is a placeholder. In reality, you must carefully parse bytes.
      const adminAuthority = new PublicKey(data.slice(8, 8+32));
      const monopolyMintKey = new PublicKey(data.slice(40, 40+32));
      const rollMintKey = new PublicKey(data.slice(72, 72+32));
      const stakeVault = new PublicKey(data.slice(104, 104+32));
      const feeVault = new PublicKey(data.slice(136, 136+32));
      const totalStaked = Number(data.readBigUInt64LE(168));
      const rewardRate = Number(data.readBigUInt64LE(176));
      const lastUpdate = Number(data.readBigUInt64LE(184));
      const globalRPT = data.readBigUInt64LE(192); // if stored as u128, need 16 bytes
      const feePercentage = data[208]; // if a single byte

      // NOTE: If global_reward_per_token is u128, you need two 64-bit reads.
      // Placeholder assuming 16 bytes starting at 192:
      const low = data.readBigUInt64LE(192);
      const high = data.readBigUInt64LE(200);
      const globalRewardPerToken = (high << 64n) + low;

      setPoolState({
        admin_authority: adminAuthority.toBase58(),
        monopoly_mint: monopolyMintKey.toBase58(),
        roll_mint: rollMintKey.toBase58(),
        monopoly_stake_vault: stakeVault.toBase58(),
        monopoly_fee_vault: feeVault.toBase58(),
        total_staked: totalStaked,
        reward_rate: rewardRate,
        last_update_timestamp: lastUpdate,
        global_reward_per_token: globalRewardPerToken,
        fee_percentage: feePercentage,
      });
    } catch (e) {
      console.error(e);
    }
    setLoading(false);
  }, [connection, derivePoolPDA]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  return { poolState, loading, refresh };
}

